import { clearCache } from '#library/function.library.js';
import { isEqual } from '#library/object.library.js';
import { isDefined, isPlainObject, isSymbol, isUndefined } from '#library/assertion.library.js';
import { ownKeys, unwrap, hasOwn } from '#library/primitive.library.js';
import { sym } from './support.symbol.js';
import { getRuntime } from './support.runtime.js';
import { setProperty, logWarn } from './support.util.js';
// Import the live enums and their mutable state from the enum module
import { STATE, REGISTRIES, DEFAULTS } from './support.enum.js';
const rt = getRuntime();
/** @internal Return the runtime's reset-hook set */
const resetHooks = () => rt.resetHooks;
/** @internal Register a hook to be called when the registry is reset */
export function onRegistryReset(hook) {
    resetHooks().add(hook);
}
/** Reset all extendable registries to their original built-in defaults */
export function registryReset() {
    ownKeys(STATE).forEach(name => {
        const state = STATE[name];
        const defaults = DEFAULTS[name];
        const target = unwrap(REGISTRIES[name]);
        // 1. Purge all own-properties from state and target (if configurable and extensible)
        [state, target].filter(obj => obj != null).forEach(obj => {
            const symInternal = Object.values(sym); // these are the Symbols we want to preserve
            Reflect.ownKeys(obj)
                .filter(key => !isSymbol(key) || !symInternal.includes(key))
                .forEach(key => {
                const desc = Object.getOwnPropertyDescriptor(obj, key);
                if (desc?.configurable)
                    delete obj[key];
            });
        });
        // 2. Restore defaults using property descriptors to preserve accessors/configurability
        Reflect.ownKeys(defaults).forEach(key => {
            const desc = Object.getOwnPropertyDescriptor(defaults, key);
            if (desc) {
                [state, target].filter(obj => obj != null).forEach(obj => {
                    if (Object.isExtensible(obj)) {
                        Object.defineProperty(obj, key, desc);
                    }
                    else {
                        // For non-extensible objects, only update if property exists
                        if (hasOwn(obj, key)) {
                            Object.defineProperty(obj, key, desc);
                        }
                        else {
                            logWarn(`[tempo] registryReset: Cannot define property '${String(key)}' on non-extensible object (property does not exist)`, {}, obj);
                        }
                    }
                });
            }
        });
        if (target)
            clearCache(target);
        if (REGISTRIES[name])
            clearCache(REGISTRIES[name]);
        clearCache(state);
    });
    if (rt.state) {
        rt.state.pluginsDb.terms.length = 0;
        rt.state.pluginsDb.plugins.length = 0;
    }
    rt.extensions.length = 0;
    rt.installed.clear();
    // Trigger all registered reset hooks
    const hooks = resetHooks();
    hooks.forEach(hook => hook());
}
/** update a global registry with new discoverable data */
export function registryUpdate(name, data) {
    const registry = REGISTRIES[name];
    const state = STATE[name];
    const target = (unwrap(registry) ?? registry);
    if (!isDefined(target))
        return;
    const merge = (tgt, src, st) => {
        Object.entries(src).forEach(([key, val]) => {
            const current = tgt[key];
            if (isUndefined(current)) { // only add if key does not exist
                setProperty(tgt, key, val);
                if (isDefined(st))
                    st[key] = val;
                return;
            }
            if (Array.isArray(current) && Array.isArray(val)) { // append to existing arrays (e.g. MONTH_DAY.locales)
                const arr = current;
                val.forEach(v => {
                    if (!arr.some((existing) => isEqual(existing, v))) {
                        arr.push(v);
                        if (isDefined(st)) {
                            if (!Array.isArray(st[key]))
                                setProperty(st, key, []);
                            if (!st[key].some((existing) => isEqual(existing, v)))
                                st[key].push(v);
                        }
                    }
                });
                return;
            }
            if (isPlainObject(current) && isPlainObject(val)) { // deep merge for objects (e.g. MONTH_DAY.timezones)
                if (isDefined(st) && !isPlainObject(st[key]))
                    setProperty(st, key, {});
                merge(current, val, isDefined(st) ? st[key] : undefined);
                return;
            }
        });
    };
    merge(target, data, state);
    clearCache(target);
    if (registry)
        clearCache(registry);
    if (state)
        clearCache(state);
}
